<template>
    <div class="c-cis col-xl-8 col-lg-8 col-md-8">
        <!-- Title — month-highlighted prompt, swapped for overdue-status copy -->
        <h2
            v-if="saleLineItem.status.id === 6"
            :style="titleStyle"
        >
            Please bring us up to speed on these uncollected amounts:
        </h2>
        <h2 v-else :style="titleStyle">
            Please provide information about the service activity in
            <span class="red" :style="accentStyle">{{ currentMonth }}</span>:
        </h2>

        <!-- ===== Type-record selector (Collected vs Not yet, etc.) ===== -->
        <div :style="choicePairStyle">
            <label
                v-for="(t, index) in typesRecordCollected"
                :key="`${t.id}__${saleLineItem.period}__${index}`"
                :for="`type_${t.id}`"
                :style="typeChoiceStyle(typeRecord === t.id, t.id)"
                @click="onTypeClick(t.id)"
            >
                <input
                    type="radio"
                    :id="`type_${t.id}`"
                    name="type_id"
                    :value="t.id"
                    :checked="typeRecord === t.id"
                    :style="srOnly"
                    @change="$emit('update:type-record', Number($event.target.value)); $emit('select-type', $event)"
                />
                <span :style="typeChoiceIconStyle(typeRecord === t.id, t.id)">
                    {{ t.id === 10 ? '$' : '–' }}
                </span>
                <div>
                    <div :style="typeChoiceLabelStyle">{{ t.name }}</div>
                    <div :style="typeChoiceSubStyle(typeRecord === t.id)">
                        {{ t.id === 10 ? 'Record the amounts collected' : 'Carry to next period' }}
                    </div>
                </div>
            </label>
        </div>

        <!-- "Not collected" (typeRecord === 11) — capture reason + notes
             inline. Matches the prototype's StepC_NoPayment "what's the
             status of payment" branch, and replaces what was previously
             a flow that just-submitted with reason=11 and (for status=6
             sales only) auto-navigated to OverdueExplanationScreen. -->
        <CCard v-if="Number(typeRecord) === 11" :extra-style="cardStyle">
            <div :style="eyebrowStyleNoPay">You answered "not yet"</div>
            <h3 :style="noPaymentTitleStyle">
                What's the status of payment for
                <span class="red" :style="accentStyle">{{ currentMonth }}</span>?
            </h3>
            <p :style="noPaymentLeadStyle">
                Pick the closest reason and add any notes. The outstanding balance
                rolls forward and we'll ask again next period.
            </p>

            <div :style="noPaymentGridStyle">
                <label
                    v-for="r in noPaymentReasons"
                    :key="`np_${r.id}`"
                    :for="`np_reason_${r.id}`"
                    :style="reasonOptionStyle(noPaymentReason === r.id)"
                    @click="onNoPaymentReasonClick(r.id)"
                >
                    <input
                        type="radio"
                        :id="`np_reason_${r.id}`"
                        name="no_payment_reason_id"
                        :value="r.id"
                        :checked="noPaymentReason === r.id"
                        :style="srOnly"
                        @change="onNoPaymentReasonClick(r.id)"
                    />
                    <span :style="reasonRadioDotStyle(noPaymentReason === r.id)">
                        <span v-if="noPaymentReason === r.id" :style="reasonRadioInnerStyle" />
                    </span>
                    <span :style="reasonOptionLabelStyle">{{ r.name }}</span>
                </label>
            </div>

            <div :style="explanationFieldStyle">
                <label :style="labelStyle">Notes</label>
                <textarea
                    v-model="saleLineItem.explanation"
                    :style="textareaStyle"
                    placeholder="Anything TAG should know about why nothing was collected…"
                    @input="(e) => onExplanationInput(e.target.value)"
                ></textarea>
            </div>

            <p :style="hintStyle">
                Press <strong>Next →</strong> at the bottom to save and move on.
            </p>
        </CCard>

        <!-- ===== Financial form — only when "Collected" is selected ===== -->
        <CCard v-if="Number(typeRecord) === 10" :extra-style="cardStyle">
            <!-- Branch 1: existing (non-new) project/retainer -->
            <template v-if="!saleLineItem.is_new_project && !saleLineItem.is_new_retainer">
                <div :style="amountGridStyle">
                    <CField label="Invoice number" optional>
                        <div
                            v-if="saleLineItem.status.id === 1"
                            :style="inputWrapStyle"
                        >
                            <input
                                :disabled="typeRecord !== 1"
                                v-model="saleLineItem.invoice_number"
                                :style="bareInputStyle"
                                @input="(e) => updateInvoiceNumber(e.target.value, saleLineItem.sale.id, 'invoice_number', saleLineItem.id)"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">{{ saleLineItem.invoice_number }}</div>
                    </CField>

                    <CField label="Amount">
                        <div :style="readonlyValueStyle">${{ numberWithCommas(saleLineItem.amount) }}</div>
                    </CField>

                    <CField label="Outstanding balance">
                        <div :style="readonlyValueStyle">${{ saleLineItem.outstanding_balance }}</div>
                    </CField>

                    <CField label="Required payment">
                        <div :style="readonlyValueStyle">
                            ${{ requiredPayment }}
                        </div>
                    </CField>

                    <CField label="Collected">
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.collected"
                                :disabled="typeRecord !== 10"
                                :change="(value, _key) => { updateInputCollected(value, saleLineItem.id, _key); }"
                                :key_input="'collected'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>

                    <CField
                        v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1"
                        label="Hard cost"
                    >
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.hard_cost"
                                :disabled="typeRecord !== 10 || !saleLineItem.collected || saleLineItem.collected <= 0"
                                :change="(value, _key) => { updateInputHardCost(value, saleLineItem.id, _key); }"
                                :key_input="'hard_cost'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>
                </div>

                <!-- Hard-cost cap explanation (existing branch) -->
                <div
                    v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1
                        && Number(saleLineItem.hard_cost) > Number(saleLineItem.amount) * Number(saleLineItem.deal_line_item.deal.cap)"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="(e) => onExplanationInput(e.target.value)"
                    ></textarea>
                </div>
            </template>

            <!-- Branch 2: new project / new retainer -->
            <template v-else>
                <div :style="amountGridStyle">
                    <CField label="Invoice number" optional>
                        <div
                            v-if="saleLineItem.status.id === 1"
                            :style="inputWrapStyle"
                        >
                            <input
                                :disabled="typeRecord !== 10"
                                v-model="saleLineItem.invoice_number"
                                :style="bareInputStyle"
                                @input="(e) => updateProjectInvoiceNumber(e.target.value, saleLineItem.deal_line_item.id, 'invoice_number', saleLineItem.is_new_retainer, saleLineItem.period)"
                            />
                        </div>
                        <div v-else :style="readonlyValueStyle">{{ saleLineItem.invoice_number }}</div>
                    </CField>

                    <CField label="Amount">
                        <div :style="readonlyValueStyle">${{ numberWithCommas(saleLineItem.amount) }}</div>
                    </CField>

                    <CField label="Outstanding balance">
                        <div :style="readonlyValueStyle">${{ saleLineItem.outstanding_balance }}</div>
                    </CField>

                    <CField label="Required payment">
                        <div :style="readonlyValueStyle">
                            ${{ requiredPayment }}
                        </div>
                    </CField>

                    <CField label="Collected">
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.collected"
                                :disabled="typeRecord !== 10"
                                :change="(value, _key) => { updateProjectInputCollected(value, saleLineItem.deal_line_item.id, _key, saleLineItem.is_new_retainer, saleLineItem.period); }"
                                :key_input="'collected'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>

                    <CField
                        v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1"
                        label="Hard cost"
                    >
                        <div :style="currencyWrapStyle">
                            <CurrencyInput
                                :options="currencyInputSetting"
                                :value="saleLineItem.hard_cost"
                                :disabled="typeRecord !== 10 || !saleLineItem.collected || saleLineItem.collected <= 0"
                                :change="(value, _key) => { updateProjectInputHardCost(value, saleLineItem.deal_line_item.id, _key, saleLineItem.is_new_retainer, saleLineItem.period); }"
                                :key_input="'hard_cost'"
                                :isLoading="isLoading"
                            />
                        </div>
                    </CField>
                </div>

                <!-- Hard-cost cap explanation (new branch — pre-refactor had two
                     identical blocks here back-to-back; we keep that quirk for
                     parity with the original DOM and any QA selectors). -->
                <div
                    v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1
                        && Number(saleLineItem.hard_cost) > Number(saleLineItem.amount) * Number(saleLineItem.deal_line_item.deal.cap)"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="(e) => onExplanationInput(e.target.value)"
                    ></textarea>
                </div>
                <div
                    v-if="saleLineItem.deal_line_item.deal.commission_type_id !== 1
                        && Number(saleLineItem.hard_cost) > Number(saleLineItem.amount) * Number(saleLineItem.deal_line_item.deal.cap)"
                    :style="explanationFieldStyle"
                >
                    <label :style="labelStyle">Explanation</label>
                    <textarea
                        v-model="saleLineItem.explanation"
                        :style="textareaStyle"
                        @input="(e) => onExplanationInput(e.target.value)"
                    ></textarea>
                </div>
            </template>

            <!-- Dark commission card -->
            <div :style="commissionCardStyle">
                <div :style="commissionRowStyle">
                    <div>
                        <div :style="commissionLabelStyle">Your commission this period</div>
                        <div :style="commissionValueStyle">${{ saleLineItem.commission_owed }}</div>
                    </div>
                    <div :style="commissionBreakdownStyle">
                        <div>${{ saleLineItem.collected }} collected</div>
                        <div>− ${{ saleLineItem.hard_cost }} hard cost</div>
                        <div :style="commissionBreakdownLineStyle">
                            ${{ saleLineItem.eligible_amount }} × {{ saleLineItem.procent }}%
                        </div>
                    </div>
                </div>
            </div>
        </CCard>

        <!-- ===== Less-than-invoice reason picker ===== -->
        <CCard
            v-if="Number(typeRecord) !== 11
                && collectedAmountModified
                && Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance) < Number(saleLineItem.amount)
                && Number(saleLineItem.amount) - (Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance))
                   > Number(saleLineItem.amount - saleLineItem.outstanding_balance) * threshold
                && saleLineItem.collected
                && saleLineItem.amount"
            :extra-style="cardStyle"
        >
            <h3 :style="reasonHeadingStyle">
                Please specify why the collected amount is less than the invoice amount
            </h3>
            <div :style="reasonGridStyle">
                <label
                    v-for="(reason, index) in collectedLessAmount"
                    :key="`collected_reason_less_${reason.id}_${index}`"
                    :for="`reason_less_${reason.id}`"
                    :style="reasonOptionStyle(selectedReason === reason.id)"
                >
                    <input
                        type="radio"
                        :id="`reason_less_${reason.id}`"
                        :checked="selectedReason === reason.id"
                        name="reason_id"
                        :value="+reason.id"
                        :style="srOnly"
                        @change="$emit('update:selected-reason', Number($event.target.value)); $emit('change-reason', $event)"
                    />
                    <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                        <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                    </span>
                    <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                </label>
            </div>
            <div :style="explanationFieldStyle">
                <label :style="labelStyle">Explanation</label>
                <textarea
                    v-model="saleLineItem.explanation"
                    :style="textareaStyle"
                    @input="(e) => onExplanationInput(e.target.value)"
                ></textarea>
            </div>
        </CCard>

        <!-- ===== More-than-invoice reason picker ===== -->
        <CCard
            v-else-if="Number(typeRecord) !== 11
                && collectedAmountModified
                && Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance) > Number(saleLineItem.amount)
                && Number(saleLineItem.collected) + Number(saleLineItem.outstanding_balance) - Number(saleLineItem.amount)
                   > Number(saleLineItem.amount - saleLineItem.outstanding_balance) * threshold
                && saleLineItem.collected
                && saleLineItem.amount"
            :extra-style="cardStyle"
        >
            <h3 :style="reasonHeadingStyle">
                Please specify why the collected amount is more than the invoice amount
            </h3>
            <div :style="reasonGridStyle">
                <label
                    v-for="(reason, index) in amountLessCollected"
                    :key="`collected_reason_more_${reason.id}_${index}`"
                    :for="`reason_more_${reason.id}`"
                    :style="reasonOptionStyle(selectedReason === reason.id)"
                >
                    <input
                        type="radio"
                        :id="`reason_more_${reason.id}`"
                        :checked="selectedReason === reason.id"
                        name="reason_id"
                        :value="+reason.id"
                        :style="srOnly"
                        @change="$emit('update:selected-reason', Number($event.target.value)); $emit('change-reason', $event)"
                    />
                    <span :style="reasonRadioDotStyle(selectedReason === reason.id)">
                        <span v-if="selectedReason === reason.id" :style="reasonRadioInnerStyle" />
                    </span>
                    <span :style="reasonOptionLabelStyle">{{ reason.name }}</span>
                </label>
            </div>
            <div :style="explanationFieldStyle">
                <label :style="labelStyle">Explanation</label>
                <textarea
                    v-model="saleLineItem.explanation"
                    :style="textareaStyle"
                    @input="(e) => onExplanationInput(e.target.value)"
                ></textarea>
            </div>
        </CCard>
    </div>
</template>

<script>
import CurrencyInput from "./CurrencyInput";

import CCard from "../c-design/CCard.vue";
import CField from "../c-design/CField.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_INK,
    C_INK2,
    C_MUTED,
    C_HAIR,
    C_CREAM,
    C_RED,
    C_GREEN,
    FONT_DISPLAY,
    FONT_BODY,
} from "../c-design/tokens";

/**
 * Extracted from EditSaleLineItem.vue (lines 1026–1632 pre-refactor).
 *
 * The "Please provide information about the service activity in {month}"
 * screen — gated by `is_collected_section && !is_not_value_for_collected`
 * in the parent. Lets the user pick a type_record radio (e.g. 1, 2, 10, 11)
 * and, when type_record === 10, fills out invoice number / collected /
 * hard cost / explanation; emits the two amount-mismatch reason follow-ups
 * when collected diverges from invoice amount past `threshold`.
 *
 * Sibling of the 5 already-extracted state screens
 * (OverdueExplanationScreen, ServiceDetailsSidebar, AmountDiffReasonScreen,
 * NoServiceProvidedScreen, BillingDelayedScreen). Same prop+emit pattern.
 *
 * The `saleLineItem` prop is mutated in place by v-model on
 * `saleLineItem.invoice_number` / `saleLineItem.explanation`. This matches
 * pre-refactor behavior — the host EditSaleLineItem already relies on
 * object-by-reference propagation.
 *
 * Variant-C visual port (Stage 5 of wizard redesign): type_record radios
 * become CChoice-style label cards backed by hidden <input type="radio">;
 * the financial form lives in its own CCard with a 2-col CField grid; the
 * less/more reason pickers also use the styled-label-over-radio idiom so
 * the form contract (update:type-record / change-reason / select-type)
 * stays radio-driven.
 */
export default {
    name: "CollectedInformationScreen",
    mixins: [viewportMixin],
    components: { CurrencyInput, CCard, CField },
    data() {
        return {
            // Local picker for the "no payment received" branch. The
            // production backend's selected_reason for type_record=11 is
            // always 11 (the parent's onSelectSaleLineItemType hardcodes
            // this), so this is purely a UX signal — it shapes the
            // explanation textarea's placeholder and gives the user
            // closure on the decision. Persists across re-renders of the
            // same brand because the component instance is keyed on
            // brand index by the parent.
            noPaymentReason: null,
        };
    },
    props: {
        saleLineItem: { type: Object, required: true },
        currentMonth: { type: String, default: "" },
        typeRecord: { default: null },
        selectedReason: { default: null },
        typesRecordCollected: { type: Array, default: () => [] },
        collectedLessAmount: { type: Array, default: () => [] },
        amountLessCollected: { type: Array, default: () => [] },
        currencyInputSetting: { type: Object, default: () => ({}) },
        isLoading: { type: Boolean, default: false },
        threshold: { default: null },
        collectedAmountModified: { type: Boolean, default: false },
        numberWithCommas: { type: Function, required: true },
        updateInvoiceNumber: { type: Function, required: true },
        updateProjectInvoiceNumber: { type: Function, required: true },
        updateInputCollected: { type: Function, required: true },
        updateProjectInputCollected: { type: Function, required: true },
        updateInputHardCost: { type: Function, required: true },
        updateProjectInputHardCost: { type: Function, required: true },
        updateExplanation: { type: Function, required: true },
        updateProjectExplanation: { type: Function, required: true },
    },
    computed: {
        // The financial form section is anchored to a "Collected" type
        // (id === 10) being present in the picker. Pre-refactor this was
        // expressed by gating the form inside the radio loop with
        // `v-if="t.id === 10"`; we hoist it to a top-level computed so the
        // form lives once outside the loop (cleaner DOM, same behaviour).
        hasCollectedType() {
            return Array.isArray(this.typesRecordCollected)
                && this.typesRecordCollected.some((t) => t.id === 10);
        },
        // Safe difference of amount − outstanding_balance. The backend
        // sometimes ships these as comma-separated strings ("1,157.35"),
        // and `1575 - "1,157.35"` evaluates to NaN. parseLoose strips
        // commas / dollar signs / whitespace so the subtraction is
        // numeric. Same helper lives in MainQuestionsScreen.vue.
        requiredPayment() {
            const parseLoose = (v) => {
                if (v === null || v === undefined || v === '') return 0;
                const n = parseFloat(String(v).replace(/[,$\s]/g, ''));
                return Number.isFinite(n) ? n : 0;
            };
            const diff = parseLoose(this.saleLineItem.amount)
                - parseLoose(this.saleLineItem.outstanding_balance);
            return this.numberWithCommas(diff);
        },
        // Reasons offered when the user marks the period "Not collected".
        // Reuses the existing `collectedLessAmount` prop (ids 19/20/21/24)
        // because that's the system-grade reason list for "collected
        // amount falls short of invoiced" — and 0-collected is just an
        // extreme of that case. Sourced from EditSaleLineItem.vue's
        // `collected_less_amount` data block.
        noPaymentReasons() {
            return Array.isArray(this.collectedLessAmount) ? this.collectedLessAmount : [];
        },
        // ===== c-design style maps =====
        titleStyle() {
            return {
                margin: this.isMobile ? '0 0 18px' : '0 0 22px',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '24px' : '28px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-0.6px' : '-0.7px',
                lineHeight: 1.15,
                color: C_INK,
            };
        },
        accentStyle() {
            return { color: C_RED };
        },
        choicePairStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '12px',
                marginBottom: this.isMobile ? '18px' : '24px',
            };
        },
        cardStyle() {
            return {
                marginBottom: this.isMobile ? '14px' : '18px',
                padding: this.isMobile ? '22px' : '32px 36px',
            };
        },
        amountGridStyle() {
            // 6 fields here: Invoice number / Amount / Outstanding balance /
            // Required payment / Collected / Hard cost. "Outstanding balance"
            // and "Required payment" labels don't fit in half a phone
            // viewport, so on mobile we drop to 1-col and let inputs span
            // full width. Desktop keeps the 2-col grid.
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '14px',
            };
        },
        inputWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '14px',
                background: C_CREAM,
                padding: '0 16px',
            };
        },
        bareInputStyle() {
            return {
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                padding: '14px 0',
                fontSize: '16px',
                fontFamily: FONT_BODY,
                fontWeight: 500,
                color: C_INK,
                width: '100%',
            };
        },
        currencyWrapStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '14px',
                background: C_CREAM,
                padding: '4px 16px',
            };
        },
        readonlyValueStyle() {
            return {
                padding: '14px 16px',
                fontFamily: FONT_DISPLAY,
                fontSize: '16px',
                fontWeight: 600,
                color: C_INK,
                background: '#fff',
                border: `1px solid ${C_HAIR}`,
                borderRadius: '14px',
            };
        },
        commissionCardStyle() {
            return {
                marginTop: this.isMobile ? '18px' : '24px',
                padding: this.isMobile ? '20px' : '22px 26px',
                background: C_INK,
                color: '#fff',
                borderRadius: this.isMobile ? '22px' : '16px',
            };
        },
        commissionRowStyle() {
            return {
                display: 'flex',
                flexDirection: this.isMobile ? 'column' : 'row',
                alignItems: this.isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: this.isMobile ? '8px' : '18px',
            };
        },
        commissionLabelStyle() {
            return {
                fontSize: '11px',
                color: '#FFFFFF99',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        commissionValueStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '40px' : '44px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-1.4px' : '-1.5px',
                lineHeight: 1,
                marginTop: '6px',
            };
        },
        commissionBreakdownStyle() {
            return {
                textAlign: this.isMobile ? 'left' : 'right',
                fontSize: this.isMobile ? '11.5px' : '12px',
                color: '#FFFFFFB0',
                lineHeight: 1.6,
            };
        },
        commissionBreakdownLineStyle() {
            return {
                borderTop: '1px solid #FFFFFF33',
                marginTop: '4px',
                paddingTop: '4px',
            };
        },
        reasonHeadingStyle() {
            return {
                margin: this.isMobile ? '0 0 14px' : '0 0 18px',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '18px' : '20px',
                fontWeight: 700,
                letterSpacing: '-0.4px',
                color: C_INK,
            };
        },
        reasonGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '8px' : '10px',
            };
        },
        reasonRadioInnerStyle() {
            return {
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#fff',
                display: 'inline-block',
            };
        },
        reasonOptionLabelStyle() {
            return {
                fontSize: '14px',
                fontWeight: 600,
                color: 'inherit',
            };
        },
        srOnly() {
            return {
                position: 'absolute',
                width: '1px',
                height: '1px',
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
                whiteSpace: 'nowrap',
                border: '0',
            };
        },
        explanationFieldStyle() {
            return { marginTop: '18px' };
        },
        labelStyle() {
            return {
                display: 'block',
                fontSize: '12px',
                color: C_INK2,
                fontWeight: 600,
                marginBottom: '6px',
            };
        },
        textareaStyle() {
            return {
                width: '100%',
                minHeight: '90px',
                padding: '14px',
                border: `1.5px solid ${C_HAIR}`,
                borderRadius: '14px',
                background: C_CREAM,
                resize: 'vertical',
                fontFamily: FONT_BODY,
                fontSize: '14px',
                color: C_INK,
                outline: 'none',
            };
        },
        typeChoiceLabelStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: '17px',
                fontWeight: 700,
                letterSpacing: '-0.2px',
            };
        },
        // ===== "Not collected" inline reason + notes section =====
        eyebrowStyleNoPay() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontWeight: 600,
                marginBottom: '6px',
            };
        },
        noPaymentTitleStyle() {
            return {
                margin: 0,
                fontFamily: FONT_DISPLAY,
                fontSize: '24px',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                lineHeight: 1.15,
                color: C_INK,
            };
        },
        noPaymentLeadStyle() {
            return {
                margin: '8px 0 18px',
                fontSize: '14px',
                color: C_MUTED,
                lineHeight: 1.55,
            };
        },
        noPaymentGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '8px' : '10px',
            };
        },
        hintStyle() {
            return {
                margin: '14px 0 0',
                fontSize: '12px',
                color: C_MUTED,
                fontStyle: 'italic',
                lineHeight: 1.5,
            };
        },
    },
    methods: {
        // Belt-and-braces: in addition to the hidden radio's natural
        // <label for=…> association, an explicit @click handler on the
        // label dispatches the same emit pair. This guards against the
        // case where inner spans intercept the click and the radio
        // isn't toggled (which was the symptom the user saw when
        // picking "Not collected").
        onTypeClick(id) {
            if (this.typeRecord === id) return;
            this.$emit('update:type-record', Number(id));
            // Mirror the @change event shape the parent's
            // onSelectSaleLineItemType expects (`e.target.value`).
            this.$emit('select-type', { target: { value: Number(id) } });
        },
        // Local-only — picks the "no payment received" sub-reason. Backend
        // selected_reason stays at 11 (set by the parent); the chosen
        // sub-reason colours the highlighted chip and contextualises the
        // notes textarea for the agency manager reading the submission.
        onNoPaymentReasonClick(id) {
            this.noPaymentReason = id;
        },
        typeChoiceStyle(selected, typeId) {
            const greenTint = typeId === 10 && selected;
            return {
                textAlign: 'left',
                padding: '20px 22px',
                background: selected ? (greenTint ? '#EAF6EF' : C_CREAM) : '#fff',
                border: `2px solid ${selected ? (greenTint ? C_GREEN : C_INK) : C_HAIR}`,
                borderRadius: '16px',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                transition: 'all 120ms',
                position: 'relative',
            };
        },
        typeChoiceIconStyle(selected, typeId) {
            const accent = typeId === 10 ? C_GREEN : C_INK;
            return {
                width: '44px',
                height: '44px',
                borderRadius: '14px',
                flexShrink: 0,
                background: selected ? accent : '#F5F1EA',
                color: selected ? '#fff' : C_INK2,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 700,
            };
        },
        typeChoiceSubStyle(selected) {
            return {
                fontSize: '12px',
                color: selected ? '#5B6270' : C_MUTED,
                marginTop: '2px',
            };
        },
        reasonOptionStyle(selected) {
            return {
                textAlign: 'left',
                padding: '14px 16px',
                background: selected ? C_INK : '#fff',
                color: selected ? '#fff' : C_INK,
                border: `1.5px solid ${selected ? C_INK : C_HAIR}`,
                borderRadius: '12px',
                fontFamily: FONT_BODY,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                position: 'relative',
            };
        },
        reasonRadioDotStyle(selected) {
            return {
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: `1.5px solid ${selected ? '#fff' : C_HAIR}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            };
        },
        // Pre-refactor the same explanation @input branch (regular sale vs
        // project sale) appeared in 5 different places. Folded into one
        // private helper called from each <textarea>.
        onExplanationInput(value) {
            if (
                !this.saleLineItem.is_new_project &&
                !this.saleLineItem.is_new_retainer
            ) {
                this.updateExplanation(
                    value,
                    this.saleLineItem.id,
                    "explanation",
                    null
                );
            } else {
                this.updateProjectExplanation(
                    value,
                    this.saleLineItem.deal_line_item.id,
                    "explanation",
                    this.saleLineItem.is_new_retainer,
                    this.saleLineItem.period
                );
            }
        },
    },
};
</script>

<style scoped>
.c-cis {
    padding: 8px 16px 32px;
}
.c-cis .red {
    color: #E63946;
}
</style>

<style>
/* CurrencyInput (vue-currency-input lib) renders a bare <input> with
 * browser-default width. Inside our flex currencyWrapStyle wrappers
 * it refused to fill the parent, leaving the input visually anchored
 * at ~150px while the surrounding wrap stretched past the viewport on
 * iPhone (the Collected / Hard cost fields ran off the right edge).
 * Force the input to fill its flex parent. Scoped to .c-cis so we
 * don't touch the legacy `.wizard .form-field input.currency-input
 * { width: 95px }` rule that's still in play for non-c-design code. */
.c-cis .currency-input {
    width: 100% !important;
    min-width: 0;
    box-sizing: border-box;
    border: none;
    outline: none;
    background: transparent;
    padding: 14px 0;
    font-size: 16px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    color: #1F1B17;
}
</style>
